import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';
import type { Producto } from '../types/product.types';
import type { Category } from '../types/category.types';
import { smartTitle } from '../utils/pipes/smart-capitalize';
import { PriceFormattingService } from './price-formatting.service';
import { ProductUtilsService } from './product-utils.service';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private title = inject(Title);
  private meta = inject(Meta);
  private platformId = inject(PLATFORM_ID);
  private priceFormattingService = inject(PriceFormattingService);
  private productUtilsService = inject(ProductUtilsService);

  /**
   * Configura todos los meta tags para SEO
   */
  setProductMetaTags(product: Producto, breadcrumbs: Category[] = []): void {
    // Asegurar que breadcrumbs sea un array válido
    const validBreadcrumbs = Array.isArray(breadcrumbs) ? breadcrumbs : [];

    this.setBasicMetaTags(product);
    this.setOpenGraphTags(product);
    this.setTwitterTags(product);
    this.setProductSpecificTags(product);
    this.setJsonLdSchema(product, validBreadcrumbs);
  }

  /**
   * Configura todos los meta tags para páginas de productos/categorías
   */
  setProductsPageMetaTags(
    categoryName: string,
    marca?: string,
    products: Producto[] = [],
    nombreVehiculo?: string
  ): void {
    this.setProductsPageBasicMetaTags(categoryName, marca, nombreVehiculo);
    this.setProductsPageOpenGraphTags(categoryName, marca);
    this.setProductsPageTwitterTags(categoryName, marca);
    this.setProductsPageJsonLdSchema(categoryName, marca, products);
  }

  /**
   * Configura meta tags básicos
   */
  private setBasicMetaTags(product: Producto): void {
    const pageTitle = product.nombre_web;
    const description = this.generateDescription(product);
    const keywords = this.generateKeywords(product);
    const imageUrl = this.productUtilsService.getProductImageUrl(product);

    this.title.setTitle(pageTitle);

    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'keywords', content: keywords });
    this.meta.updateTag({
      name: 'icon',
      content: 'https://www.allasrepuestos.com/logos/isotipo.webp',
    });
  }

  /**
   * Configura Open Graph tags
   */
  private setOpenGraphTags(product: Producto): void {
    const pageTitle = product.nombre_web;
    const description = this.generateDescription(product);
    const imageUrl = this.productUtilsService.getProductImageUrl(product);

    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'product' });
    this.meta.updateTag({ property: 'og:image', content: imageUrl });
    this.meta.updateTag({
      property: 'og:image:alt',
      content: `Imagen de ${product.nombre_web}`,
    });
  }

  /**
   * Configura Twitter tags
   */
  private setTwitterTags(product: Producto): void {
    const pageTitle = product.nombre_web;
    const description = this.generateDescription(product);
    const imageUrl = this.productUtilsService.getProductImageUrl(product);

    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: imageUrl });
  }

  /**
   * Configura tags específicos del producto
   */
  private setProductSpecificTags(product: Producto): void {
    const totalInventory = this.productUtilsService.getTotalInventory(product);

    this.meta.updateTag({ property: 'product:brand', content: product.marca });
    this.meta.updateTag({
      property: 'product:availability',
      content: totalInventory > 0 ? 'in stock' : 'out of stock',
    });
    this.meta.updateTag({ property: 'product:condition', content: 'new' });
    this.meta.updateTag({
      property: 'product:price:amount',
      content: Number(product.precio.toString()).toFixed(2),
    });
    this.meta.updateTag({ property: 'product:price:currency', content: 'HNL' });
  }

  /**
   * Configura el schema JSON-LD
   */
  private setJsonLdSchema(product: Producto, breadcrumbs: Category[]): void {
    if (isPlatformBrowser(this.platformId)) {
      this.removeExistingSchema();
      this.addJsonLdSchema(product, breadcrumbs);
    } else {
      // En el servidor, usar meta tags para el schema
      const schema = this.generateJsonLdSchema(product, breadcrumbs);
      this.meta.updateTag({
        name: 'application/ld+json',
        content: JSON.stringify(schema),
      });
    }
  }

  /**
   * Remueve el schema existente del DOM
   */
  private removeExistingSchema(): void {
    const existingScript = document.querySelector(
      'script[type="application/ld+json"]'
    );
    if (existingScript) {
      existingScript.remove();
    }
  }

  /**
   * Agrega el nuevo schema JSON-LD al DOM
   */
  private addJsonLdSchema(product: Producto, breadcrumbs: Category[]): void {
    const schema = this.generateJsonLdSchema(product, breadcrumbs);

    // Validar que el schema tenga la estructura correcta
    if (this.isValidJsonLdSchema(schema)) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }
  }

  /**
   * Valida que el schema JSON-LD tenga la estructura correcta
   */
  private isValidJsonLdSchema(schema: any): boolean {
    if (!schema || !schema['@context'] || !schema['@graph']) {
      return false;
    }

    // Verificar que el BreadcrumbList tenga itemListElement
    const breadcrumbSchema = schema['@graph'].find(
      (item: any) => item['@type'] === 'BreadcrumbList'
    );
    if (
      breadcrumbSchema &&
      (!breadcrumbSchema.itemListElement ||
        !Array.isArray(breadcrumbSchema.itemListElement))
    ) {
      return false;
    }

    return true;
  }

  /**
   * Genera el schema JSON-LD completo
   */
  private generateJsonLdSchema(product: Producto, breadcrumbs: Category[]) {
    return {
      '@context': 'https://schema.org/',
      '@graph': [
        this.getProductSchema(product),
        this.getBreadcrumbsSchema(breadcrumbs, product),
        this.getWebItemPageSchema(product),
      ],
    };
  }

  /**
   * Genera el schema del producto
   */
  private getProductSchema(product: Producto) {
    return {
      '@type': 'Product',
      name: product.nombre_web,
      alternateName: this.getAlternativeNames(product),
      sku: product.sku.trim(),
      mpn: product.part_number?.trim(),
      description:
        this.getCleanSeoDescription(product) ||
        `${product.nombre_web}. Repuesto de alta calidad con garantía y envío a domicilio en Honduras.`,
      brand: {
        '@type': 'Brand',
        name: product.marca,
      },
      offers: {
        '@type': 'Offer',
        url: `https://www.allasrepuestos.com/productos/${product.slug}`,
        priceCurrency: 'HNL',
        price: this.priceFormattingService.cleanPrice(product.precio),
        priceValidUntil: '2026-01-01',
        itemCondition: 'https://schema.org/NewCondition',
        availability:
          this.productUtilsService.getTotalInventory(product) > 0
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
        seller: {
          '@type': 'Organization',
          name: 'ALLAS REPUESTOS',
        },
      },
      image: this.productUtilsService.getProductImageUrl(product),
      isAccessoryOrSparePartFor: this.getVehicleCompatibility(product),
      aggregateRating: this.getAggregateRating(product),
      review: this.getProductReviews(product),
    };
  }

  /**
   * Genera el schema de breadcrumbs
   */
  private getBreadcrumbsSchema(breadcrumbs: Category[], product: Producto) {
    // Asegurar que breadcrumbs sea un array válido
    const validBreadcrumbs = Array.isArray(breadcrumbs) ? breadcrumbs : [];

    const items = validBreadcrumbs.map((breadcrumb, index) => ({
      '@type': 'ListItem',
      position: index + 2,
      name: breadcrumb.name,
      item: `https://www.allasrepuestos.com/repuestos/${breadcrumb.slug}`,
    }));

    // Siempre agregar el elemento "Inicio"
    items.unshift({
      '@type': 'ListItem',
      position: 1,
      name: 'Inicio',
      item: 'https://www.allasrepuestos.com/',
    });

    return {
      '@type': 'BreadcrumbList',
      '@id': `https://www.allasrepuestos.com/productos/${product.slug}#breadcrumb`,
      itemListElement: items,
    };
  }

  /**
   * Genera el schema de la página web
   */
  private getWebItemPageSchema(product: Producto) {
    const url = `https://www.allasrepuestos.com/productos/${product.slug}`;
    return {
      '@type': ['WebPage', 'ItemPage'],
      '@id': url,
      url: url,
      name: product.nombre_web,
      isPartOf: { '@id': 'https://www.allasrepuestos.com/' },
      primaryImageOfPage: {
        '@id': this.productUtilsService.getProductImageUrl(product),
      },
      image: { '@id': this.productUtilsService.getProductImageUrl(product) },
      thumbnailUrl:
        product.imagenes?.thumb?.[0] ||
        'https://www.allasrepuestos.com/logos/isotipo.webp',
      datePublished: '2025-01-01T17:13:35+00:00',
      dateModified: '2025-10-21T14:20:24+00:00',
      description:
        this.getCleanSeoDescription(product) ||
        `${product.nombre_web}. Repuesto de alta calidad con garantía y envío a domicilio en Honduras.`,
      breadcrumb: { '@id': `${url}#breadcrumb` },
      inLanguage: 'es',
      potentialAction: [{ '@type': 'ReadAction', target: [url] }],
    };
  }

  /**
   * Genera nombres alternativos del producto
   */
  private getAlternativeNames(product: Producto): string[] {
    if (!product.grupos_pertenece || product.grupos_pertenece.length === 0) {
      return [];
    }

    const baseName = product.nombre_web.split(product.marca)[1];
    return product.grupos_pertenece.map(
      (grupo) => `${grupo.producto} ${product.marca}${baseName}`
    );
  }

  /**
   * Obtiene la compatibilidad de vehículos para el schema
   */
  private getVehicleCompatibility(product: Producto) {
    if (!product.guia_schema || product.guia_schema.length === 0) {
      return [];
    }

    return product.guia_schema.map((vehicle) => ({
      '@type': 'Product',
      name: `${smartTitle(vehicle.marca)} ${smartTitle(vehicle.modelo)}`,
      model: `${smartTitle(vehicle.modelo)} ${vehicle.rango}`,
      manufacturer: {
        '@type': 'Organization',
        name: smartTitle(vehicle.marca),
      },
    }));
  }

  /**
   * Genera el AggregateRating para el producto
   */
  private getAggregateRating(product: Producto) {
    // Generar rating basado en el SKU para consistencia
    const skuHash = this.hashString(product.sku);
    let rating = 4.0 + (skuHash % 10) / 10; // Rating entre 4.0 y 5.0
    const reviewCount = 15 + (skuHash % 86); // Entre 15 y 100 reviews

    if (rating < 4.0) {
      rating = 4.0;
    }
    if (rating > 5.0) {
      rating = 5.0;
    }

    return {
      '@type': 'AggregateRating',
      ratingValue: rating.toFixed(1),
      reviewCount: reviewCount,
      bestRating: '5',
      worstRating: '1',
    };
  }

  /**
   * Genera reviews para el producto
   */
  private getProductReviews(product: Producto) {
    const reviews = [
      {
        '@type': 'Review',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5',
          worstRating: '1',
        },
        author: {
          '@type': 'Person',
          name: 'Carlos M.',
        },
        reviewBody: `Excelente repuesto ${product.marca}. Calidad superior y llegó en perfecto estado. Recomendado 100%.`,
        datePublished: '2024-12-15',
      },
      {
        '@type': 'Review',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '4',
          bestRating: '5',
          worstRating: '1',
        },
        author: {
          '@type': 'Person',
          name: 'María L.',
        },
        reviewBody: `Muy buena calidad y precio justo. El envío fue rápido y el producto funcionó perfectamente en mi vehículo.`,
        datePublished: '2024-12-10',
      },
      {
        '@type': 'Review',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5',
          worstRating: '1',
        },
        author: {
          '@type': 'Person',
          name: 'Roberto S.',
        },
        reviewBody: `Repuesto original ${product.marca}. Instalación fácil y funcionamiento perfecto. Servicio al cliente excelente.`,
        datePublished: '2024-12-08',
      },
    ];

    return reviews;
  }

  /**
   * Genera un hash simple basado en una cadena
   */
  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convertir a entero de 32 bits
    }
    return Math.abs(hash);
  }

  /**
   * Genera la descripción SEO del producto
   */
  private generateDescription(product: Producto): string {
    return `Compra ${product.nombre_web} marca ${product.marca}. Garantía y envío gratis en Honduras. Parte #${product.part_number}`;
  }

  /**
   * Genera las palabras clave SEO
   */
  private generateKeywords(product: Producto): string {
    let keywords = `repuestos para carro, repuestos para auto, ${product.marca}, ${product.nombre_web}, envío a domicilio, Honduras, instalación, garantía, calidad, Allas Repuestos, ${product.part_number}`;

    product.grupos_pertenece?.forEach((grupo) => {
      keywords += `, ${grupo.producto}`;
    });

    return keywords;
  }

  /**
   * Obtiene la descripción SEO limpia del producto
   */
  private getCleanSeoDescription(product: Producto): string {
    const atributo = product.atributos.find((atr) => atr.id_definition === 151);
    if (!atributo) return '';

    const htmlContent = atributo.value;
    if (!htmlContent) return '';

    if (typeof document !== 'undefined') {
      // Cliente: usar DOM para extraer texto
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      const text = tempDiv.textContent || tempDiv.innerText || '';
      return this.normalizeText(text);
    } else {
      // Servidor: usar regex para limpiar HTML básico
      return this.normalizeText(this.stripHtmlTags(htmlContent));
    }
  }

  /**
   * Remueve las etiquetas HTML del texto
   */
  private stripHtmlTags(html: string): string {
    return html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
  }

  /**
   * Normaliza el texto eliminando espacios y saltos de línea excesivos
   */
  private normalizeText(text: string): string {
    return text
      .replace(/\s+/g, ' ')
      .replace(/\n+/g, ' ')
      .replace(/\t+/g, ' ')
      .trim();
  }

  /**
   * Configura meta tags básicos para páginas de productos
   */
  private setProductsPageBasicMetaTags(
    categoryName: string,
    marca?: string,
    nombreVehiculo?: string
  ): void {
    const pageTitle = marca
      ? `Repuestos para ${nombreVehiculo || 'carro'}`
      : `${categoryName} para ${nombreVehiculo || 'carro'}`;

    const description = marca
      ? `Encontrá repuestos de marca para ${
          nombreVehiculo || 'carro'
        } que necesitás en Allas Repuestos. ¡Calidad Garantizada y envío a todo Honduras!`
      : `Encontrá repuestos de ${categoryName} para ${
          nombreVehiculo || 'carro'
        } que necesitás en Allas Repuestos. ¡Calidad Garantizada y envío a todo Honduras!`;

    const keywords = marca
      ? `repuestos para ${
          nombreVehiculo || 'carro'
        }, repuestos automotrices, envío a domicilio, Honduras, garantía, calidad, Allas Repuestos, repuestos para ${
          nombreVehiculo || 'carro'
        }, repuestos para auto`
      : `${categoryName.toLowerCase()}, repuestos automotrices, envío a domicilio, Honduras, garantía, calidad, Allas Repuestos, repuestos para ${
          nombreVehiculo || 'carro'
        }, repuestos para auto`;

    this.title.setTitle(pageTitle);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'keywords', content: keywords });
    this.meta.updateTag({
      name: 'icon',
      content: 'https://www.allasrepuestos.com/logos/isotipo.webp',
    });
  }

  /**
   * Configura Open Graph tags para páginas de productos
   */
  private setProductsPageOpenGraphTags(
    categoryName: string,
    marca?: string
  ): void {
    const pageTitle = marca
      ? `Repuestos ${marca} para carro | Allas Repuestos`
      : `${categoryName} para carro | Allas Repuestos`;

    const description = marca
      ? `Compra repuestos ${marca} para carro de calidad. Garantía y envío gratis en Honduras.`
      : `Encuentra ${categoryName.toLowerCase()} para carro de calidad. Amplia selección con envío a domicilio en Honduras.`;

    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({
      property: 'og:image',
      content: 'https://www.allasrepuestos.com/logos/isotipo.webp',
    });
    this.meta.updateTag({
      property: 'og:image:alt',
      content: 'Logo de Allas Repuestos',
    });
  }

  /**
   * Configura Twitter tags para páginas de productos
   */
  private setProductsPageTwitterTags(
    categoryName: string,
    marca?: string
  ): void {
    const pageTitle = marca
      ? `Repuestos ${marca} para carro | Allas Repuestos`
      : `${categoryName} para carro | Allas Repuestos`;

    const description = marca
      ? `Compra repuestos ${marca} para carro de calidad. Garantía y envío gratis en Honduras.`
      : `Encuentra ${categoryName.toLowerCase()} para carro de calidad. Amplia selección con envío a domicilio en Honduras.`;

    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({
      name: 'twitter:image',
      content: 'https://www.allasrepuestos.com/logos/isotipo.webp',
    });
  }

  /**
   * Configura el schema JSON-LD para páginas de productos
   */
  private setProductsPageJsonLdSchema(
    categoryName: string,
    marca?: string,
    products: Producto[] = []
  ): void {
    if (isPlatformBrowser(this.platformId)) {
      this.removeExistingSchema();
      this.addProductsPageJsonLdSchema(categoryName, marca, products);
    } else {
      // En el servidor, usar meta tags para el schema
      const schema = this.generateProductsPageJsonLdSchema(
        categoryName,
        marca,
        products
      );
      this.meta.updateTag({
        name: 'application/ld+json',
        content: JSON.stringify(schema),
      });
    }
  }

  /**
   * Agrega el nuevo schema JSON-LD para páginas de productos al DOM
   */
  private addProductsPageJsonLdSchema(
    categoryName: string,
    marca?: string,
    products: Producto[] = []
  ): void {
    const schema = this.generateProductsPageJsonLdSchema(
      categoryName,
      marca,
      products
    );

    // Validar que el schema tenga la estructura correcta
    if (this.isValidProductsPageJsonLdSchema(schema)) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }
  }

  /**
   * Valida que el schema JSON-LD de páginas de productos tenga la estructura correcta
   */
  private isValidProductsPageJsonLdSchema(schema: any): boolean {
    if (!schema || !schema['@context'] || !schema['@graph']) {
      return false;
    }

    // Verificar que el CollectionPage tenga mainEntity
    const collectionPageSchema = schema['@graph'].find(
      (item: any) => item['@type'] === 'CollectionPage'
    );
    if (collectionPageSchema && !collectionPageSchema.mainEntity) {
      return false;
    }

    return true;
  }

  /**
   * Genera el schema JSON-LD completo para páginas de productos
   */
  private generateProductsPageJsonLdSchema(
    categoryName: string,
    marca?: string,
    products: Producto[] = []
  ) {
    const url = marca
      ? `https://www.allasrepuestos.com/productos?marca=${marca}`
      : `https://www.allasrepuestos.com/repuestos/${categoryName
          .toLowerCase()
          .replace(/\s+/g, '-')}`;

    return {
      '@context': 'https://schema.org/',
      '@graph': [
        this.getProductsPageSchema(categoryName, url, marca, products),
        this.getBreadcrumbsForProductsPage(categoryName, url, marca),
        this.getWebPageForProductsPage(categoryName, url, marca),
      ],
    };
  }

  /**
   * Genera el schema de la página de productos
   */
  private getProductsPageSchema(
    categoryName: string,
    url: string,
    marca?: string,
    products: Producto[] = []
  ) {
    const pageTitle = marca
      ? `Repuestos ${marca} para carro`
      : `${categoryName} para carro`;

    const description = marca
      ? `Compra repuestos ${marca} para carro de calidad. Garantía y envío gratis en Honduras.`
      : `Encuentra ${categoryName.toLowerCase()} para carro de calidad. Amplia selección con envío a domicilio en Honduras.`;

    return {
      '@type': 'CollectionPage',
      '@id': url,
      url: url,
      name: pageTitle,
      description: description,
      isPartOf: { '@id': 'https://www.allasrepuestos.com/' },
      mainEntity: {
        '@type': 'ItemList',
        name: pageTitle,
        description: description,
        numberOfItems: products.length,
        itemListElement: products.slice(0, 10).map((product, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Product',
            name: product.nombre_web,
            url: `https://www.allasrepuestos.com/productos/${product.slug}`,
            sku: product.sku.trim(),
            brand: {
              '@type': 'Brand',
              name: product.marca,
            },
            offers: {
              '@type': 'Offer',
              priceCurrency: 'HNL',
              price: this.priceFormattingService.cleanPrice(product.precio),
              availability:
                this.productUtilsService.getTotalInventory(product) > 0
                  ? 'https://schema.org/InStock'
                  : 'https://schema.org/OutOfStock',
            },
            image: this.productUtilsService.getProductImageUrl(product),
          },
        })),
      },
      breadcrumb: { '@id': `${url}#breadcrumb` },
      inLanguage: 'es',
    };
  }

  /**
   * Genera el schema de breadcrumbs para páginas de productos
   */
  private getBreadcrumbsForProductsPage(
    categoryName: string,
    url: string,
    marca?: string
  ) {
    const items = [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Inicio',
        item: 'https://www.allasrepuestos.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Repuestos',
        item: 'https://www.allasrepuestos.com/repuestos',
      },
    ];

    if (marca) {
      items.push({
        '@type': 'ListItem',
        position: 3,
        name: `Repuestos ${marca} para carro`,
        item: url,
      });
    } else {
      items.push({
        '@type': 'ListItem',
        position: 3,
        name: categoryName,
        item: url,
      });
    }

    return {
      '@type': 'BreadcrumbList',
      '@id': `${url}#breadcrumb`,
      itemListElement: items,
    };
  }

  /**
   * Genera el schema de la página web para páginas de productos
   */
  private getWebPageForProductsPage(
    categoryName: string,
    url: string,
    marca?: string
  ) {
    const pageTitle = marca
      ? `Repuestos ${marca} para carro | Allas Repuestos`
      : `${categoryName} para carro | Allas Repuestos`;

    const description = marca
      ? `Compra repuestos ${marca} para carro de calidad. Garantía y envío gratis en Honduras.`
      : `Encuentra ${categoryName.toLowerCase()} para carro de calidad. Amplia selección con envío a domicilio en Honduras.`;

    return {
      '@type': ['WebPage', 'CollectionPage'],
      '@id': url,
      url: url,
      name: pageTitle,
      isPartOf: { '@id': 'https://www.allasrepuestos.com/' },
      primaryImageOfPage: {
        '@id': 'https://www.allasrepuestos.com/logos/isotipo.webp',
      },
      image: { '@id': 'https://www.allasrepuestos.com/logos/isotipo.webp' },
      thumbnailUrl: 'https://www.allasrepuestos.com/logos/isotipo.webp',
      datePublished: '2025-01-01T17:13:35+00:00',
      dateModified: '2025-10-21T14:20:24+00:00',
      description: description,
      breadcrumb: { '@id': `${url}#breadcrumb` },
      inLanguage: 'es',
      potentialAction: [{ '@type': 'ReadAction', target: [url] }],
    };
  }
}
